import React, { useState } from 'react';
import styled from 'styled-components';
import MuiPopover from '@material-ui/core/Popover';
import Fade from '@material-ui/core/Fade';

import useTranslation from 'src/hooks/use-translation';
import { ORDER_TYPE_PICKUP } from 'shared/constants';
import RangeSliderPlum from 'components/core/range-slider/range-slider-plum';
import { GreyCapsText } from 'components/core';

import {
  AutoCompleteInputList,
  CurrentAddress,
  OrderTypeTabs,
  ShowResultsButton,
  CollapsibleSection,
  ContentWrapper,
  SectionContainer,
} from './components';
import { LocationSettingsPopoverProps } from './location-settings-popover.types';

/* --------------------------------------------------------
   Distance slider — plum-colored version
-------------------------------------------------------- */
function DistanceSliderDark({
  distance: initialValue,
  onChangeDistance,
}: {
  distance: number;
  onChangeDistance: (e: React.ChangeEvent, value: number) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState(initialValue);
  const valueText = t('locationSettings.distanceMiles', '{{localValue}} miles', { localValue });

  function handleChange(_e: React.ChangeEvent, value: number): void {
    setLocalValue(value);
  }

  return (
    <DistanceContainer>
      <PlumGreyCapsText>{t('locationSettings.currentAddress', 'Distance')}</PlumGreyCapsText>
      <RangeSliderPlum
        aria-labelledby='distance-text'
        getAriaLabel={() => valueText}
        getAriaValueText={() => valueText}
        min={1}
        max={50}
        onChange={handleChange}
        onChangeCommitted={onChangeDistance}
        value={localValue}
      />
      <DistanceText id='distance-text'>
        {t('locationSettings.shopFromDispensariesWithin', 'Shop from dispensaries within')} <b>{valueText}</b>
      </DistanceText>
    </DistanceContainer>
  );
}

/* --------------------------------------------------------
   Popover presentational — white bg, plum accents
-------------------------------------------------------- */
export function LocationSettingsPopoverDark({
  open,
  anchorEl,
  orderType,
  distance,
  addressFieldFocused,
  currentAddressString,
  onClose,
  onSave,
  onChangeDistance,
  onChangeOrderType,
  onChangeTempLocation,
}: LocationSettingsPopoverProps): JSX.Element {
  return (
    <PlumPopover
      open={open}
      onClose={onClose}
      anchorEl={anchorEl.current}
      data-cy='location-settings-popover'
      data-test='location-settings-popover'
      data-testid='location-settings-popover'
      TransitionComponent={Fade}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 50 }}
      style={{ overflow: 'visible' }}
    >
      <ContentWrapper>
        <OrderTypeTabs orderType={orderType} onChangeOrderType={onChangeOrderType} />
        <SectionContainer>
          {currentAddressString && currentAddressString.length > 0 && (
            <CurrentAddress currentAddressString={currentAddressString} />
          )}
          <AutoCompleteInputList onChangeTempLocation={onChangeTempLocation} />
        </SectionContainer>
        <CollapsibleSection collapsed={addressFieldFocused}>
          {orderType === ORDER_TYPE_PICKUP && (
            <DistanceSliderDark distance={distance} onChangeDistance={onChangeDistance} />
          )}
          <ShowResultsButton onClick={onSave} />
        </CollapsibleSection>
      </ContentWrapper>
    </PlumPopover>
  );
}

/* --------------------------------------------------------
   Styled components
-------------------------------------------------------- */
const PlumPopover = styled(MuiPopover)`
  .MuiPopover-paper {
    overflow: visible;
    margin-top: 21px;
    border-radius: 14px;

    ${({ theme }) => theme.breakpoints.down('xs')} {
      margin-top: 5px;
      max-width: none;
      left: 5px !important;
      right: 5px !important;
      top: 91px !important;
    }
  }

  &.MuiPopover-root {
    background-color: rgba(69, 78, 80, 0.35);
    z-index: 1100 !important;
  }

  /* ---- Tabs ---- */
  .MuiTabs-indicator {
    height: 3px;
    background-color: var(--rds-plum);
  }
  .MuiTab-textColorInherit {
    color: ${({ theme }) => theme.colors.grey[30]};
  }
  .MuiTab-textColorInherit.Mui-selected {
    color: var(--rds-plum);
  }

  /* ---- Show Results button ---- */
  button[data-testid='show-results-button'] {
    background-color: var(--rds-plum);
    color: #ffffff;
    &:hover {
      background-color: #3d2030;
    }
  }
`;

const DistanceContainer = styled.div`
  padding: 30px 0;
  margin: 0 30px;
  border-top: ${({ theme }) => `1px solid ${theme.colors.blueGrey[95] as string}`};
  .MuiSlider-root {
    padding-top: 15px;
    padding-bottom: 18px;
  }
`;

const PlumGreyCapsText = styled(GreyCapsText)`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[70]};
`;

const DistanceText = styled.div`
  color: var(--dcm-color-text-primary, ${({ theme }) => theme.colors.primaryBlack});
  font-size: 14px;
`;
